import React from 'react'

import { UI2FormSectionStatus, WizardSectionRegistration } from './wizard-controller'

export interface WizardProgressStep {
  id: string
  title?: string
  isActive: boolean
  isCompleted: boolean
  /** True iff the user has been to this section at least once — the section is tappable in the progress indicator. */
  isVisited: boolean
  /** True iff this section has ever been advanced past. Stays true even after the user jumps back into the section — sections check this to know they are being revisited. */
  wasEverCompleted: boolean
}

export interface IUI2WizardContext {
  /** True when the parent form is in wizard mode. Sections read this to decide whether to render footer buttons and to hide themselves when inactive. */
  isWizard: boolean
  /** The form's wizard mode. Sections read this to know whether to render themselves as complete or hide themselves. */
  mode: 'wizard' | 'stacked'
  /** ID of the currently active section. Sections compare against their own id to know whether to render. */
  activeSectionId: string | null
  /** Section registers on mount; deregisters on unmount. Registration order = declaration order in the form's children. */
  register: (registration: Omit<WizardSectionRegistration, 'index'>) => void
  deregister: (id: string) => void
  /** Section calls this when the user submits it, or when automatic advance conditions are met. */
  advance: (sectionId: string) => void
  /** Section calls this when the user taps Skip. */
  skip: (sectionId: string) => void
  /** Whether a Back button should be enabled — true when at least one completed section is on the stack. */
  canGoBack: boolean
  back: () => void
  /** Jump the wizard directly to a previously visited section (progress-indicator tap). No-op if the target has not been visited. */
  jumpTo: (sectionId: string) => void
  /**
   * Reveal a section regardless of whether the user has completed it. Backward
   * targets rewind the completed stack; forward targets reorder the queue so
   * the section becomes active while leaving already-completed sections
   * alone. Used by save-flow focusFirstError navigation to bring an errored
   * field into view; progress-indicator tap should keep using `jumpTo` so
   * the completed-only constraint is preserved.
   */
  revealSection: (sectionId: string) => void
  /** Snapshot of every registered section in declaration order, annotated with visit/completion state. Empty in default mode. */
  progressSteps: WizardProgressStep[]
  /**
   * Per-section status keyed by section id, for sections that bind `value`.
   * A read-only projection of the wizard state — the wizard never reads it
   * back. Empty in default mode, where sections derive their own status from
   * reported field errors.
   */
  sectionStatuses: Record<string, UI2FormSectionStatus>
  /** True when advancing the active section would empty the queue — sections use this to relabel Next as "Complete". */
  isFinalStep: boolean
  /** True when every real section has been dequeued (activeId is null with a non-empty completed stack). Forms render their success block in this state. */
  isSuccessState: boolean
  /**
   * True while the wizard is silently replaying prefilled sections on first
   * load. Sections should hide their body and footer and render a spinner
   * while this is true — the wizard chrome stays visible.
   */
  isReplaying: boolean
}

const DEFAULT_CTX: IUI2WizardContext = {
  isWizard: false,
  mode: 'wizard',
  activeSectionId: null,
  register: () => {},
  deregister: () => {},
  advance: () => {},
  skip: () => {},
  canGoBack: false,
  back: () => {},
  jumpTo: () => {},
  revealSection: () => {},
  progressSteps: [],
  sectionStatuses: {},
  isFinalStep: false,
  isSuccessState: false,
  isReplaying: false,
}

export const UI2WizardContext = React.createContext<IUI2WizardContext>(DEFAULT_CTX)
